import React from "react";
import {
  action,
  createMachine,
  reduce,
  state,
  transition,
  Transition
} from "robot3";
import {toInProgressResult} from "../transforms";
import {summarizeResult} from "../helpers";
import {ReviewPlanControls, ReviewPlanStep} from "../steps/review-plan";
import {
  ResultsStep,
  InProgressControls,
  SuccessControls,
  FailureControls
} from "../steps/results";
import {OrganizationStepper} from "../steps/common";
import {StateMachineContext} from "./StateMachineContext";
import {CreateEvent} from "./CreateEvent";
import {FailureEvent} from "./FailureEvent";
import {RetryEvent} from "./RetryEvent";
import {StepBackEvent} from "./StepBackEvent";
import {SuccessEvent} from "./SuccessEvent";

const context = (
  input: Omit<StateMachineContext, "stepper" | "step" | "controls">
): StateMachineContext => ({
  ...input,
  stepper: () => null,
  step: () => null,
  controls: () => null
});

export const stateMachine = createMachine(
  {
    "review-plan": state(
      transition(
        "create",
        "in-progress",
        action<StateMachineContext, CreateEvent>((ctx, {newEntity}) => {
          ctx.clearFeedback();
          // Fire the mutation asynchronously
          // The result will be handled by the component which will send success/failure events
          ctx.createOrganization(newEntity).catch(err => {
            console.error("Failed to fire mutation:", err);
          });
        }),
        reduce<StateMachineContext, CreateEvent>((ctx, {newEntity}) => ({
          ...ctx,
          stepper: () => (
            <OrganizationStepper activeStep={1} reviewPlanCompleted />
          ),
          step: () => <ResultsStep result={toInProgressResult(newEntity)} />,
          controls: () => <InProgressControls onRetry={ctx.retry} />
        }))
      )
    ),
    // Requires Transition<string> because this state handles multiple event types: "progress", "success", "failure"
    "in-progress": state<Transition<string>>(
      transition(
        "success",
        "success",
        reduce<StateMachineContext, SuccessEvent>((ctx, {source, result}) => {
          const summary = summarizeResult(source, result);

          window.pendo?.track("Organization Created", {
            created: 1,
            mapped: summary.mapped,
            failed: 0
          });

          ctx.showFeedback({
            type: "success",
            message: ctx.translate("create-organization.feedback.success"),
            autoHide: true,
            onClose: ctx.clearFeedback
          });

          return {
            ...ctx,
            stepper: () => (
              <OrganizationStepper
                activeStep={1}
                reviewPlanCompleted
                resultsCompleted
              />
            ),
            step: () => <ResultsStep result={result} />,
            controls: () => <SuccessControls />
          };
        })
      ),
      transition(
        "failure",
        "failure",
        reduce<StateMachineContext, FailureEvent>((ctx, {source, result}) => {
          const summary = summarizeResult(source, result);

          window.pendo?.track("Create Organization Failed", {
            created: 0,
            mapped: summary.mapped,
            failed: 1
          });

          ctx.showFeedback({
            type: "error",
            message:
              summary.failed?.message ||
              ctx.translate("create-organization.errors.generic"),
            autoHide: true,
            onClose: ctx.clearFeedback
          });

          return {
            ...ctx,
            stepper: () => (
              <OrganizationStepper
                activeStep={1}
                reviewPlanCompleted
                resultsError
              />
            ),
            step: () => <ResultsStep result={result} />,
            controls: () => (
              <FailureControls onStepBack={ctx.stepBack} onRetry={ctx.retry} />
            )
          };
        })
      )
    ),
    "success": state(),
    // Requires Transition<string> because this state handles multiple event types: "step-back", "retry"
    "failure": state<Transition<string>>(
      transition(
        "step-back",
        "review-plan",
        reduce<StateMachineContext, StepBackEvent>(ctx => ({
          ...ctx,
          stepper: () => <OrganizationStepper activeStep={0} />,
          step: () => <ReviewPlanStep />,
          controls: () => <ReviewPlanControls onCreate={ctx.create} />
        }))
      ),
      transition(
        "retry",
        "in-progress",
        action<StateMachineContext, RetryEvent>((ctx, {newEntity}) => {
          ctx.clearFeedback();
          // Fire the mutation again asynchronously
          ctx.createOrganization(newEntity).catch(err => {
            console.error("Failed to fire retry mutation:", err);
          });
        }),
        reduce<StateMachineContext, RetryEvent>((ctx, {newEntity}) => ({
          ...ctx,
          stepper: () => (
            <OrganizationStepper activeStep={1} reviewPlanCompleted />
          ),
          step: () => <ResultsStep result={toInProgressResult(newEntity)} />,
          controls: () => <InProgressControls onRetry={ctx.retry} />
        }))
      )
    )
  },
  (input: Omit<StateMachineContext, "stepper" | "step" | "controls">) => {
    const ctx = context(input);
    // Set initial render functions for review-plan state
    return {
      ...ctx,
      stepper: () => <OrganizationStepper activeStep={0} />,
      step: () => <ReviewPlanStep />,
      controls: () => <ReviewPlanControls onCreate={ctx.create} />
    };
  }
);
